import { getRailsClient } from "sj-ui-toolkit/src/client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ProgressReportRow {
  id?: string | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  roleName?: string | null;
  status?: string | null;
  requiredProgressPercent?: string | null;
  timeSpent?: string | null;
  completedAt?: string | null;
  levelName?: string | null;
  createdAt?: string | null;
  lastEngagement?: string | null;
  deletedAt?: string | null;
  archived?: string | null;
}

export interface ProgressReportResponse {
  roleEnrollments: ProgressReportRow[];
  totalPages: number;
}

export interface ProgressReportFilterOptions {
  jobRole: string[];
  title: string[];
  manager: string[];
  managersEmail: string[];
  employeeNumber: string[];
  securityChampion: string[];
  department: string[];
  businessUnit: string[];
  team: string[];
  company: string[];
  country: string[];
  path: string[];
}

export interface ProgressReportParams {
  page: number;
  per_page: number;
  sortable_column?: string | null;
  order_type?: "asc" | "desc";
  query?: string | null;
  from?: number | null;
  to?: number | null;
  status?: string[];
  level?: string[];
  category?: string[];
  job_role?: string[];
  title?: string[];
  department?: string[];
  business_unit?: string[];
  country?: string[];
  company?: string[];
  manager?: string[];
  security_champion?: string[];
  team?: string[];
  managers_email?: string[];
  employee_number?: string[];
  path?: string[];
  unenrolled?: string[];
  archived?: string[];
}

// Shape of lazyState.filters for this report — dates become ISO strings after
// JSON round-trip through localStorage.
export interface ProgressReportFilters {
  global?: { value: string | null };
  dateRange?: { value: [string | null, string | null] | null };
  status?: { value: string[] };
  level?: { value: string[] };
  category?: { value: string[] };
  jobRole?: { value: string[] };
  title?: { value: string[] };
  manager?: { value: string[] };
  managersEmail?: { value: string[] };
  employeeNumber?: { value: string[] };
  securityChampion?: { value: string[] };
  department?: { value: string[] };
  businessUnit?: { value: string[] };
  team?: { value: string[] };
  company?: { value: string[] };
  country?: { value: string[] };
  path?: { value: string[] };
  unenrolled?: { value: string[] };
  archived?: { value: string[] };
}

// User progress detail types

interface RoleEnrollment {
  id?: string | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  userId?: string | null;
}

export interface LessonActivity {
  name?: string | null;
  completedAt?: string | null;
  result?: string | null;
  passed?: boolean | null;
  hackedu?: boolean | null;
}

export interface UserProgressData {
  roleEnrollment: RoleEnrollment;
  lessons: LessonActivity[];
}

export interface UserProfileStatsData {
  totalPoints?: number | null;
  lastMonthPoints?: number | null;
  totalTime?: number | null;
  lastMonthTotalTime?: number | null;
  currentLevel?: { levelName?: string | null } | null;
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

export const loadProgressReportPage = async (
  params: ProgressReportParams
): Promise<ProgressReportResponse> => {
  const API = `/api/internal_api/v1/admin/reports/progress`;
  const { data } = await getRailsClient().get<ProgressReportResponse>(API, { params });
  return data;
};

export const downloadProgressReportCsv = async (
  params: Omit<ProgressReportParams, "page" | "per_page">
): Promise<string> => {
  const API = `/api/internal_api/v1/admin/reports/progress/download`;
  const { data } = await getRailsClient().get<string>(API, { params });
  return data;
};

export const loadProgressFilterOptions = async (): Promise<ProgressReportFilterOptions> => {
  const API = `/api/internal_api/v1/admin/reports/progress_filters_component`;
  const { data } = await getRailsClient().get<ProgressReportFilterOptions>(API);
  return data;
};

export const loadUserProgress = async (id: string): Promise<UserProgressData> => {
  const API = `/api/internal_api/v1/admin/reports/progress/${id}`;
  const { data } = await getRailsClient().get<UserProgressData>(API);
  return data;
};

export const loadUserProfileStats = async (userId: string): Promise<UserProfileStatsData> => {
  const API = `/api/internal_api/atlas/v1/admin/users/${userId}`;
  const { data } = await getRailsClient().get<UserProfileStatsData>(API);
  return data;
};
